'use client';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useMapContext } from '../../../contexts/map-context';
import useLandMap from '../../../hooks/useLandMap';
import Image from 'next/image';
import { useAccount } from 'wagmi';

/* styled elements */
const FilterPanel = styled.div<{ iswalletconnected: string; isexpanded: string; }>`
    background: #130F0FCC;
    border: 1px solid #D4D4D4;
    box-sizing: border-box;
    border-radius: 8px;
    width: ${(props: { iswalletconnected: string }) => props.iswalletconnected === 'true' ? '630px' : '520px'};
    height:60px;
    transition: all .5s;
    position: absolute;
    top: 8rem;
    left: 29rem;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0rem 1rem;
    overflow: hidden;

    @media (min-width: 3840px) {
        left: calc(50% - 315px); /* Centering for 4K width */
    }

    @media (min-width: 2560px) and (max-width: 3839px) {
        left: calc(50% - 315px); /* Centering for 27-inch iMac */
    }

    @media (max-width: 1920px) {
        left: calc(50% - 315px); /* Centering for 1920px width */
    }

    @media (max-width: 1280px) {
        left: calc(50% - 260px); /* Centering for 1280px width */
    }

    @media (max-width: 1024px) {
        left: 10rem;
        width: 400px;
        height: ${({ isexpanded }) => (isexpanded === 'true' ? 'auto' : '220')}px;
        font-size: 16px;
        top: 35rem;
        z-index: 25;
    }

    @media (max-width: 768px) {
        top: 40rem;
        left: 2rem;
        width: 230px;
        font-size: 16px;
        height: ${({ isexpanded }) => (isexpanded === 'true' ? 'auto' : '220')}px;
        z-index: 25;
    }
`;

const FilterContent = styled.div`
    display: flex;
    flex-direction: row;
    padding: 1rem 0;
    padding-bottom: .5rem;
    color: white;

    @media (max-width: 1024px) {
        flex-direction: column;
        font-size:16px;
        width:100%;
    }
`;
const FilterHeader = styled.div`
    width: 100%;
    display: none;
    justify-content: space-between;
    align-items: center;
    
    @media (max-width: 1024px) {
        padding: 1rem 0;
        display: flex;
    }
`;
const Title = styled.div`
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    text-align: center;
    color: #FFF;
`;
const FilterActions = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2rem;
`;
const ExpandIcon = styled.div`
  cursor: pointer;
`;

const filterDataUnconnectWalletState = [
    {
        text: 'All Lands',
        searchOption: ''
    }, {
        text: 'Available to Buy',
        searchOption: 'openforminting'
    },
    {
        text: 'Available to Lease',
        searchOption: 'privatehub'
    }
];

const filterDataConnectWalletState = [
    {
        text: 'All Lands',
        searchOption: ''
    }, {
        text: 'My land',
        searchOption: ''
    }, {
        text: 'Available to Buy',
        searchOption: 'openforminting'
    },
    {
        text: 'Available to Lease',
        searchOption: 'privatehub'
    }
];

export const CenterFilter = () => {
    const { state, dispatch } = useMapContext();
    const { address: account } = useAccount();
    const [expand, setExpand] = useState(true);
    const setSearchOptions = (newOptions: any) => dispatch({ type: 'UPDATE_SEARCH_OPTIONS', payload: { newOptions } });

    const setSearchOption = (option: string) => {
        const newOptions = { ...state.searchOptions };
        newOptions[option] = !newOptions[option];
        setSearchOptions(newOptions);
    };

    const searchByWallet = () => {
        if (account) {
            const newOptions = { ...state.searchOptions };
            newOptions.walletAddress = account;
            setSearchOptions(newOptions);
        }
    };
    const clearSearchByWallet = () => {
        const newOptions = { ...state.searchOptions }
        newOptions.walletAddress = ''
        setSearchOptions(newOptions)
    }

    const handleClearFilters = () => {
        const newOptions = {
            shiboshiZone: false,
            privatehub: false,
            diamond: false,
            platinum: false,
            gold: false,
            silver: false,
            openforbid: false,
            minPos: {
                x: null,
                y: null,
            },
            maxPos: {
                x: null,
                y: null,
            },

            walletAddress: '',

            clearFilters: true
        };

        setSearchOptions(newOptions);
    };

    return (
        <FilterPanel iswalletconnected={account ? 'true' : 'false'} isexpanded={expand.toString()}>
            <FilterHeader>
                <Title>Land Availability</Title>
                <FilterActions>
                    <ExpandIcon onClick={() => setExpand(prev => !prev)}>
                        {expand ? <Image src={'/images/chevron-down-black.png'} alt="Selected Icon" width={25} height={25} /> : <Image src={'/images/chevron-up-black.png'} alt="Selected Icon" width={25} height={25} />}
                    </ExpandIcon>
                </FilterActions>
            </FilterHeader>
            <FilterContent className='w-full text-white font-quicksand md:space-x-0 lg:space-x-6 '>
                {(account ? filterDataConnectWalletState : filterDataUnconnectWalletState).map((item, index) => (
                    <div
                        className={`flex items-center cursor-pointer mb-2 rounded-lg text-white transition duration-200`}
                        key={`filter ${index}`}
                        onClick={() => {
                            if (item.text === "My land") {
                                if (state.searchOptions.walletAddress === account) {
                                    clearSearchByWallet();
                                    handleClearFilters()
                                } else {
                                    searchByWallet();
                                }
                            } else if (item.text !== "All Lands") {
                                setSearchOption(item.searchOption);
                            }
                        }}
                    >
                        <div className="flex items-center justify-center mr-2">
                            <button className={`border bg-white hover:opacity-80 w-5 h-5 flex items-center justify-center`}>
                                {(item.text === "All Lands") ||
                                    (item.text === "My land" && state.searchOptions.walletAddress === account) ||
                                    state.searchOptions[item.searchOption] ? (
                                    <Image src={'/images/map/icons/check.svg'} alt="Selected Icon" width={18} height={18} />
                                ) : null}
                            </button>
                        </div>
                        <p className="text-white hover:text-[#d99e63]">{item.text}</p>
                    </div>
                ))}
            </FilterContent>
        </FilterPanel>
    );
};

export default React.memo(CenterFilter);